<template>
  <div class="screen_shell">
    <header class="screen_header">
      <div class="screen_header__tag">运行监测中</div>
      <div class="screen_header__title_group">
        <h1>基于遥感时序数据的乡村建设与生态变化监测可视分析系统</h1>
      </div>
      <div class="screen_header__time_panel">
        <span>监测时间</span>
        <strong>{{ monitor_date_label }}</strong>
      </div>
    </header>

    <main class="screen_body">
      <aside class="screen_column screen_column--left">
        <MonitoringChartCard
          title="NDVI / 生态质量年度变化图"
          chart_type="ndvi_trend"
          api_endpoint="/api/ndvi"
          accent_color="#3af3cf"
          empty_message="待接入 NDVI 与生态质量年度数据"
        />
        <MonitoringChartCard
          title="建设与生态协调度散点图"
          chart_type="eco_coordination_scatter"
          api_endpoint="/api/eco-score"
          accent_color="#ffc857"
          empty_message="待接入建设与生态协调度数据"
        />
      </aside>

      <section class="screen_column screen_column--center">
        <div class="map_panel">
          <div class="map_panel__content">
            <JiangxiMap
              :display_mode="map_display_mode"
              :county_profiles="county_monitor_profiles"
              @region-click="handle_region_click"
            />
          </div>

          <div class="map_panel__legend">
            <button
              type="button"
              class="map_panel__mode_button"
              :class="{ 'map_panel__mode_button--active': map_display_mode === 'ndvi' }"
              @click="toggle_map_display_mode('ndvi')"
            >
              建设用地监测
            </button>
            <button
              type="button"
              class="map_panel__mode_button"
              :class="{ 'map_panel__mode_button--active': map_display_mode === 'farmland' }"
              @click="toggle_map_display_mode('farmland')"
            >
              耕地变化监测
            </button>
          </div>
        </div>
      </section>

      <aside class="screen_column screen_column--right">
        <MonitoringChartCard
          class="screen_column__card--water"
          title="水体变化图"
          chart_type="water_change_compare"
          api_endpoint="/api/ndwi"
          accent_color="#4bb8ff"
          empty_message="待接入水体变化监测数据"
        />

        <section class="info_card">
          <div class="info_card__header">
            <span class="info_card__marker"></span>
            <div>
              <h2>风险摘要与监测对象</h2>
            </div>
          </div>

          <div class="info_card__content">
            <div class="info_card__row">
              <span>重点监测对象</span>
              <strong>{{ info_card_target_label }}</strong>
            </div>
            <div class="info_card__row">
              <span>风险等级说明</span>
              <strong>{{ info_card_risk_label }}</strong>
            </div>
            <div class="info_card__row">
              <span>生态评估内容</span>
              <strong>{{ info_card_assessment_label }}</strong>
            </div>
          </div>
        </section>
      </aside>
    </main>

    <footer class="screen_footer">
      <section class="footer_panel footer_panel--warning">
        <div class="footer_panel__header">
          <span class="footer_panel__marker footer_panel__marker--red"></span>
          <h2>预警信息</h2>
          <button
            type="button"
            class="warning_detail_button"
            @click="warning_modal_visible = true"
          >
            查看详情
          </button>
        </div>
        <p>{{ warning_panel_text }}</p>
      </section>

      <section class="footer_panel footer_panel--suggestion">
        <button
          type="button"
          class="suggestion_button"
          @click="suggestion_modal_visible = true"
        >
          查看建议
        </button>
      </section>
    </footer>

    <div v-if="suggestion_modal_visible" class="suggestion_modal">
      <div class="suggestion_modal__mask" @click="suggestion_modal_visible = false"></div>
      <div class="suggestion_modal__panel">
        <div class="suggestion_modal__header">
          <h2>建议内容</h2>
          <button
            type="button"
            class="suggestion_modal__close"
            @click="suggestion_modal_visible = false"
          >
            关闭
          </button>
        </div>

        <div class="suggestion_modal__content suggestion_modal__content--formal">
          <div v-if="show_suggestion_detail" class="suggestion_report">
            <section class="suggestion_report__hero">
              <div>
                <span class="suggestion_report__eyebrow">区县监测研判</span>
                <h3>{{ selected_region_profile.name }}</h3>
                <p>{{ suggestion_modal_intro }}</p>
              </div>
              <span
                class="suggestion_report__risk_badge"
                :class="`suggestion_report__risk_badge--${suggestion_risk_tone}`"
              >
                {{ selected_region_profile.risk_label }}
              </span>
            </section>

            <section class="suggestion_report__overview">
              <div
                v-for="overview_item in suggestion_overview_items"
                :key="overview_item.label"
                class="suggestion_report__overview_item"
              >
                <span>{{ overview_item.label }}</span>
                <strong>{{ overview_item.value }}</strong>
              </div>
            </section>

            <section
              v-for="detail_section in suggestion_detail_sections"
              :key="detail_section.title"
              class="suggestion_report__section"
            >
              <div class="suggestion_report__section_header">
                <h4>{{ detail_section.title }}</h4>
              </div>
              <ul class="suggestion_report__list">
                <li
                  v-for="detail_item in detail_section.items"
                  :key="`${detail_section.title}-${detail_item}`"
                >
                  {{ detail_item }}
                </li>
              </ul>
            </section>
          </div>

          <div v-else class="suggestion_report__empty">
            <p>{{ suggestion_modal_intro }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="warning_modal_visible" class="suggestion_modal warning_modal">
      <div class="suggestion_modal__mask" @click="warning_modal_visible = false"></div>
      <div class="suggestion_modal__panel warning_modal__panel">
        <div class="suggestion_modal__header warning_modal__header">
          <div>
            <h2>预警信息详情</h2>
            <p class="warning_modal__subtitle">
              赣州市生态新闻、生态预警与环境公告综合简报
            </p>
          </div>
          <button
            type="button"
            class="suggestion_modal__close"
            @click="warning_modal_visible = false"
          >
            关闭
          </button>
        </div>

        <div class="warning_modal__content">
          <section class="warning_modal__hero">
            <div class="warning_modal__hero_header">
              <span class="warning_modal__badge">{{ warning_detail_risk_label }}</span>
            </div>
            <h3>{{ warning_detail_title }}</h3>
            <p>{{ warning_detail_summary }}</p>
            <a
              v-if="warning_detail_source_url"
              class="warning_modal__link"
              :href="warning_detail_source_url"
              target="_blank"
              rel="noreferrer"
            >
              打开原始链接
            </a>
          </section>

          <section class="warning_modal__section">
            <div class="warning_modal__section_header">
              <h4>相关信息列表</h4>
              <span>{{ warning_detail_items.length }} 条</span>
            </div>
            <div class="warning_modal__list">
              <article
                v-for="warning_item in warning_detail_items"
                :key="`${warning_item.title}-${warning_item.score}`"
                class="warning_modal__item"
              >
                <div class="warning_modal__item_header">
                  <strong>{{ warning_item.title }}</strong>
                  <span>{{ warning_item.riskLevel || '预警' }}</span>
                </div>
                <p>{{ warning_item.summary }}</p>
                <div class="warning_modal__item_footer">
                  <span>风险分值：{{ warning_item.score ?? '--' }}</span>
                  <a
                    v-if="warning_item.sourceUrl"
                    class="warning_modal__item_link"
                    :href="warning_item.sourceUrl"
                    target="_blank"
                    rel="noreferrer"
                  >
                    查看来源
                  </a>
                </div>
              </article>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonitoringChartCard from './MonitoringChartCard.vue';
import JiangxiMap from './JiangxiMap.vue';
import { resolveApiUrl } from '../utils/api';
import { analyzeCountyProfile } from '../utils/countyAnalysis';

export default {
  name: 'MonitoringDashboard',
  components: {
    MonitoringChartCard,
    JiangxiMap
  },
  data() {
    return {
      monitor_date_label: '',
      clock_timer_id: null,
      warning_refresh_timer_id: null,
      suggestion_modal_visible: false,
      warning_modal_visible: false,
      map_display_mode: 'base',
      selected_region_name: '',
      county_monitor_profiles: {},
      county_profiles_ready: false,
      warning_panel_payload: null
    };
  },
  computed: {
    selected_region_profile() {
      return this.selected_region_name
        ? this.county_monitor_profiles[this.selected_region_name] || null
        : null;
    },
    selected_region_analysis() {
      return analyzeCountyProfile(this.selected_region_profile);
    },
    show_suggestion_detail() {
      return Boolean(this.selected_region_profile && this.county_profiles_ready);
    },
    suggestion_risk_tone() {
      if (!this.selected_region_profile) {
        return 'neutral';
      }

      if (this.selected_region_profile.risk_label === '高风险') {
        return 'high';
      }

      if (this.selected_region_profile.risk_label === '中风险') {
        return 'medium';
      }

      return 'low';
    },
    warning_panel_text() {
      const warning_title = this.warning_panel_payload?.title;
      const warning_risk_level = this.warning_panel_payload?.riskLevel;
      const warning_summary = this.warning_panel_payload?.summary;

      if (!warning_title || !warning_summary) {
        return '当前为大屏联调阶段，预警栏将优先展示已抓取的生态预警、环境公告和相关新闻摘要。';
      }

      const compact_summary = warning_summary.length > 110
        ? `${warning_summary.slice(0, 110)}...`
        : warning_summary;

      return `${warning_risk_level || '预警'}：${warning_title}。${compact_summary}`;
    },
    warning_detail_title() {
      return this.warning_panel_payload?.title || '暂无预警信息';
    },
    warning_detail_risk_label() {
      return this.warning_panel_payload?.riskLevel || '待更新';
    },
    warning_detail_summary() {
      return this.warning_panel_payload?.summary || '暂未获取到赣州市生态预警、环境公告或生态新闻。';
    },
    warning_detail_source_url() {
      return this.warning_panel_payload?.sourceUrl || '';
    },
    warning_detail_items() {
      return Array.isArray(this.warning_panel_payload?.items)
        ? this.warning_panel_payload.items
        : [];
    },
    info_card_target_label() {
      if (!this.county_profiles_ready) {
        return '正在加载区县评估数据';
      }

      if (this.selected_region_profile) {
        return this.selected_region_profile.name;
      }

      return this.selected_region_name || '点击地图选择区县';
    },
    info_card_risk_label() {
      if (!this.county_profiles_ready) {
        return '评估中';
      }

      if (this.selected_region_profile) {
        return `${this.selected_region_profile.risk_label} / 综合分 ${this.selected_region_profile.risk_score_label}`;
      }

      return this.selected_region_name ? '暂无匹配数据' : '待选择';
    },
    info_card_assessment_label() {
      if (!this.county_profiles_ready) {
        return '正在汇总 NDVI、水体与协调度指标';
      }

      if (this.selected_region_profile) {
        return [
          `NDVI ${this.selected_region_profile.ndvi_label}`,
          `生态质量 ${this.selected_region_profile.eco_quality_label}`,
          `协调度 ${this.selected_region_profile.coordination_label}`
        ].join(' / ');
      }

      return this.selected_region_name
        ? '当前区县暂无联动评估数据'
        : '点击地图后显示县级生态评估';
    },
    suggestion_modal_intro() {
      if (!this.selected_region_name) {
        return '当前未选择具体区县，请选择。';
      }

      if (!this.county_profiles_ready) {
        return '正在汇总县级监测数据，请稍后。';
      }

      if (!this.selected_region_profile) {
        return '当前区县暂无可用评估数据。';
      }

      return `${this.selected_region_profile.name} 当前风险等级为 ${this.selected_region_profile.risk_label}，综合分 ${this.selected_region_profile.risk_score_label}。${this.selected_region_analysis.summary}`;
    },
    suggestion_overview_items() {
      if (!this.show_suggestion_detail) {
        return [];
      }

      return [
        {
          label: '综合风险分',
          value: this.selected_region_profile.risk_score_label
        },
        {
          label: 'NDVI',
          value: this.selected_region_profile.ndvi_label
        },
        {
          label: '生态质量',
          value: this.selected_region_profile.eco_quality_label
        },
        {
          label: '协调度',
          value: this.selected_region_profile.coordination_label
        }
      ];
    },
    suggestion_detail_sections() {
      if (!this.selected_region_profile || !this.county_profiles_ready) {
        return [];
      }

      return [
        {
          title: '主要原因',
          items: this.selected_region_analysis.reasons
        },
        {
          title: '指标快照',
          items: this.selected_region_analysis.metric_snapshot
        },
        {
          title: '建议措施',
          items: this.selected_region_analysis.suggestions
        },
        {
          title: '优先动作',
          items: this.selected_region_analysis.priority_actions
        }
      ];
    }
  },
  mounted() {
    this.update_monitor_date_label();
    this.clock_timer_id = window.setInterval(() => {
      this.update_monitor_date_label();
    }, 1000);

    this.load_warning_panel();
    this.warning_refresh_timer_id = window.setInterval(() => {
      this.load_warning_panel();
    }, 300000);

    this.load_county_monitor_profiles();
  },
  beforeUnmount() {
    if (this.clock_timer_id) {
      window.clearInterval(this.clock_timer_id);
    }

    if (this.warning_refresh_timer_id) {
      window.clearInterval(this.warning_refresh_timer_id);
    }
  },
  methods: {
    async fetch_json(api_endpoint) {
      const response = await fetch(resolveApiUrl(api_endpoint));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return response.json();
    },

    normalize_region_name(region_name) {
      return typeof region_name === 'string' ? region_name.trim() : '';
    },

    get_last_numeric_value(value_list) {
      if (!Array.isArray(value_list)) {
        return null;
      }

      for (let index = value_list.length - 1; index >= 0; index -= 1) {
        const numeric_value = Number(value_list[index]);
        if (Number.isFinite(numeric_value)) {
          return numeric_value;
        }
      }

      return null;
    },

    format_metric(metric_value, fraction_digits = 3) {
      return Number.isFinite(metric_value) ? metric_value.toFixed(fraction_digits) : '--';
    },

    build_rank_score_map(profile_list, field_name, order = 'desc') {
      const valid_profiles = profile_list.filter((profile) => Number.isFinite(profile[field_name]));
      const sorted_profiles = [...valid_profiles].sort((left_profile, right_profile) => (
        order === 'desc'
          ? right_profile[field_name] - left_profile[field_name]
          : left_profile[field_name] - right_profile[field_name]
      ));

      const rank_score_map = {};
      if (sorted_profiles.length === 1) {
        rank_score_map[sorted_profiles[0].name] = 1;
        return rank_score_map;
      }

      const denominator = Math.max(sorted_profiles.length - 1, 1);
      sorted_profiles.forEach((profile, index) => {
        rank_score_map[profile.name] = (sorted_profiles.length - index - 1) / denominator;
      });

      return rank_score_map;
    },

    resolve_risk_label(risk_score) {
      if (risk_score >= 0.67) {
        return '高风险';
      }

      if (risk_score >= 0.4) {
        return '中风险';
      }

      return '低风险';
    },

    build_county_monitor_profiles(ndvi_payload, eco_payload, ndwi_payload) {
      const county_profile_map = {};
      const city_series_name = ndvi_payload?.meta?.city_series_name;
      const ndvi_series_list = Array.isArray(ndvi_payload?.series) ? ndvi_payload.series : [];

      ndvi_series_list.forEach((series_item) => {
        const region_name = this.normalize_region_name(series_item?.name);
        if (!region_name || region_name === city_series_name) {
          return;
        }

        county_profile_map[region_name] = {
          ...(county_profile_map[region_name] || { name: region_name }),
          ndvi_latest: this.get_last_numeric_value(series_item.data)
        };
      });

      const county_eco_series = Array.isArray(eco_payload?.series)
        ? eco_payload.series.find((series_item) => Array.isArray(series_item.data))
        : null;
      const county_eco_points = Array.isArray(county_eco_series?.data) ? county_eco_series.data : [];

      county_eco_points.forEach((point_item) => {
        const region_name = this.normalize_region_name(point_item?.name);
        const metric_pair = Array.isArray(point_item?.value) ? point_item.value : [];
        if (!region_name) {
          return;
        }

        county_profile_map[region_name] = {
          ...(county_profile_map[region_name] || { name: region_name }),
          construction_area: Number(metric_pair[0]),
          coordination: Number(point_item?.coordination),
          eco_quality: Number(point_item?.eco_quality),
          ndvi_latest: Number.isFinite(Number(point_item?.ndvi))
            ? Number(point_item.ndvi)
            : county_profile_map[region_name]?.ndvi_latest
        };
      });

      const county_water_records = Array.isArray(ndwi_payload?.meta?.county_records)
        ? ndwi_payload.meta.county_records
        : [];

      county_water_records.forEach((water_record) => {
        const region_name = this.normalize_region_name(water_record?.county_name);
        if (!region_name) {
          return;
        }

        county_profile_map[region_name] = {
          ...(county_profile_map[region_name] || { name: region_name }),
          total_water_area: Number(water_record.total_water_area),
          share_ratio: Number(water_record.share_ratio),
          river_area: Number(water_record.river_area),
          reservoir_lake_area: Number(water_record.reservoir_lake_area),
          wetland_area: Number(water_record.wetland_area)
        };
      });

      const county_profile_list = Object.values(county_profile_map).map((profile) => ({
        ...profile,
        ndvi_latest: Number.isFinite(profile.ndvi_latest) ? profile.ndvi_latest : null,
        construction_area: Number.isFinite(profile.construction_area) ? profile.construction_area : null,
        coordination: Number.isFinite(profile.coordination) ? profile.coordination : null,
        eco_quality: Number.isFinite(profile.eco_quality) ? profile.eco_quality : null,
        total_water_area: Number.isFinite(profile.total_water_area) ? profile.total_water_area : null,
        share_ratio: Number.isFinite(profile.share_ratio) ? profile.share_ratio : null
      }));

      const construction_risk_map = this.build_rank_score_map(county_profile_list, 'construction_area', 'desc');
      const coordination_risk_map = this.build_rank_score_map(county_profile_list, 'coordination', 'asc');
      const ndvi_risk_map = this.build_rank_score_map(county_profile_list, 'ndvi_latest', 'asc');
      const eco_quality_risk_map = this.build_rank_score_map(county_profile_list, 'eco_quality', 'asc');
      const water_share_risk_map = this.build_rank_score_map(county_profile_list, 'share_ratio', 'desc');

      return Object.fromEntries(county_profile_list.map((profile) => {
        const risk_score = (
          (coordination_risk_map[profile.name] || 0) * 0.35
          + (ndvi_risk_map[profile.name] || 0) * 0.25
          + (construction_risk_map[profile.name] || 0) * 0.2
          + (eco_quality_risk_map[profile.name] || 0) * 0.1
          + (water_share_risk_map[profile.name] || 0) * 0.1
        );

        const normalized_profile = {
          ...profile,
          risk_score: Number(risk_score.toFixed(2)),
          risk_score_label: risk_score.toFixed(2),
          risk_label: this.resolve_risk_label(risk_score),
          ndvi_label: this.format_metric(profile.ndvi_latest, 3),
          eco_quality_label: this.format_metric(profile.eco_quality, 3),
          coordination_label: this.format_metric(profile.coordination, 3)
        };

        return [profile.name, normalized_profile];
      }));
    },

    async load_county_monitor_profiles() {
      try {
        const [ndvi_payload, eco_payload, ndwi_payload] = await Promise.all([
          this.fetch_json('/api/ndvi'),
          this.fetch_json('/api/eco-score'),
          this.fetch_json('/api/ndwi')
        ]);

        this.county_monitor_profiles = this.build_county_monitor_profiles(
          ndvi_payload,
          eco_payload,
          ndwi_payload
        );
      } catch (error) {
        console.error('Failed to load county monitoring profiles:', error);
        this.county_monitor_profiles = {};
      } finally {
        this.county_profiles_ready = true;
      }
    },

    async load_warning_panel() {
      try {
        this.warning_panel_payload = await this.fetch_json('/api/warning');
      } catch (error) {
        console.error('Failed to load warning panel data:', error);
        this.warning_panel_payload = null;
      }
    },

    handle_region_click(region_name) {
      this.selected_region_name = this.normalize_region_name(region_name);
    },

    toggle_map_display_mode(display_mode) {
      this.map_display_mode = this.map_display_mode === display_mode ? 'base' : display_mode;
    },

    update_monitor_date_label() {
      this.monitor_date_label = new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(new Date());
    }
  }
};
</script>

<style scoped>
.screen_shell {
  position: relative;
  height: 100vh;
  padding: 12px 14px 10px;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 10px;
  overflow: hidden;
}

.screen_shell::before,
.screen_shell::after {
  content: "";
  position: absolute;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  filter: blur(76px);
  opacity: 0.08;
  pointer-events: none;
}

.screen_shell::before {
  top: -100px;
  left: -80px;
  background: rgba(88, 184, 232, 0.28);
}

.screen_shell::after {
  right: -100px;
  bottom: 10px;
  background: rgba(86, 199, 179, 0.22);
}

.screen_header,
.screen_body,
.screen_footer {
  position: relative;
  z-index: 1;
}

.screen_header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 0 2px 6px;
}

.screen_header__tag {
  justify-self: start;
  position: relative;
  padding-left: 12px;
  color: var(--accent_teal);
  font-size: 11px;
  letter-spacing: 0.08em;
}

.screen_header__tag::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  transform: translateY(-50%);
}

.screen_header__title_group {
  text-align: center;
}

.screen_header h1 {
  margin: 0;
  text-align: center;
  font-size: clamp(14px, 1vw, 18px);
  line-height: 1.2;
  letter-spacing: 0.04em;
  font-weight: 500;
}

.screen_header__time_panel {
  justify-self: end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.screen_header__time_panel span {
  color: var(--text_muted);
  font-size: 9px;
  letter-spacing: 0.08em;
}

.screen_header__time_panel strong {
  color: var(--accent_cyan);
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
  font-weight: 500;
}

.screen_body {
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(250px, 3fr) minmax(420px, 5fr) minmax(250px, 3fr);
  gap: 10px;
}

.screen_column {
  min-height: 0;
}

.screen_column--left,
.screen_column--right {
  display: grid;
  gap: 10px;
}

.screen_column--left {
  grid-template-rows: repeat(2, minmax(0, 1fr));
}

.screen_column--right {
  grid-template-rows: repeat(3, minmax(0, 1fr));
}

.screen_column__card--water {
  grid-row: span 2;
}

.map_panel {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(8, 16, 25, 0.96), rgba(5, 10, 17, 0.99));
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.22);
}

.map_panel::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(98, 217, 255, 0.024) 1px, transparent 1px),
    linear-gradient(90deg, rgba(98, 217, 255, 0.024) 1px, transparent 1px);
  background-size: 52px 52px;
  opacity: 0.18;
}

.map_panel::after {
  content: "";
  position: absolute;
  left: 20px;
  right: 20px;
  top: 18px;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(79, 194, 220, 0.52), transparent);
  opacity: 0.9;
}

.map_panel__content {
  position: absolute;
  inset: 12px 12px 54px;
  z-index: 1;
}

.map_panel__legend {
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 16px;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.map_panel__mode_button {
  min-width: 116px;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: rgba(9, 18, 27, 0.78);
  color: var(--text_secondary);
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(79, 194, 220, 0.08);
}

.map_panel__mode_button:hover {
  background: rgba(14, 28, 40, 0.9);
  color: #d8e9f3;
}

.map_panel__mode_button--active {
  background: rgba(79, 194, 220, 0.16);
  color: #ecf7ff;
  box-shadow: inset 0 0 0 1px rgba(79, 194, 220, 0.24);
}

.info_card {
  position: relative;
  overflow: hidden;
  min-height: 0;
  border-radius: 10px;
  background: rgba(8, 17, 26, 0.92);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);
  padding: 12px;
}

.info_card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.018), transparent 24%);
  pointer-events: none;
}

.info_card__header,
.info_card__content {
  position: relative;
  z-index: 1;
}

.info_card__header {
  display: flex;
  gap: 8px;
  align-items: center;
  padding-bottom: 4px;
}

.info_card__marker {
  width: 4px;
  height: 14px;
  border-radius: 999px;
  background: #ff7c7c;
}

.info_card h2 {
  margin: 0;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
}

.info_card__content {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.info_card__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.info_card__row span {
  color: var(--text_secondary);
  font-size: 11px;
}

.info_card__row strong {
  max-width: 62%;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.screen_footer {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.footer_panel {
  min-height: 72px;
  border-radius: 10px;
  background: rgba(8, 16, 25, 0.88);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  padding: 10px 14px;
}

.footer_panel__header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer_panel__header h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}

.footer_panel__marker {
  width: 5px;
  height: 16px;
  border-radius: 999px;
}

.footer_panel__marker--red {
  background: var(--accent_red);
}

.footer_panel p {
  margin: 8px 0 0;
  color: var(--text_secondary);
  line-height: 1.5;
  font-size: 11px;
}

.warning_detail_button,
.suggestion_button {
  margin-left: auto;
  min-width: 96px;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: rgba(79, 194, 220, 0.12);
  color: var(--text_primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.warning_detail_button:hover,
.suggestion_button:hover {
  background: rgba(79, 194, 220, 0.18);
}

.footer_panel--suggestion {
  display: flex;
  align-items: stretch;
}

.suggestion_button {
  height: 100%;
}

.suggestion_modal {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.suggestion_modal__mask {
  position: absolute;
  inset: 0;
  background: rgba(3, 8, 14, 0.66);
}

.suggestion_modal__panel {
  position: relative;
  width: min(560px, calc(100vw - 32px));
  max-height: calc(100vh - 72px);
  border-radius: 12px;
  background: rgba(9, 17, 27, 0.98);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.32);
  padding: 16px;
  overflow: hidden;
}

.suggestion_modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.suggestion_modal__header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.suggestion_modal__close {
  padding: 6px 12px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  color: var(--text_primary);
  cursor: pointer;
}

.suggestion_modal__content {
  margin-top: 14px;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  color: var(--text_secondary);
  line-height: 1.7;
  font-size: 12px;
  padding-right: 6px;
}

.suggestion_modal__content p {
  margin: 0 0 10px;
}

.suggestion_modal__content--formal {
  padding-right: 4px;
}

.suggestion_report {
  display: grid;
  gap: 14px;
}

.suggestion_report__hero,
.suggestion_report__overview,
.suggestion_report__section,
.suggestion_report__empty {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
}

.suggestion_report__hero,
.suggestion_report__section,
.suggestion_report__empty {
  padding: 16px;
}

.suggestion_report__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.suggestion_report__eyebrow {
  display: inline-block;
  margin-bottom: 8px;
  color: var(--text_muted);
  font-size: 11px;
  letter-spacing: 0.08em;
}

.suggestion_report__hero h3 {
  margin: 0;
  color: var(--text_primary);
  font-size: 20px;
  font-weight: 500;
}

.suggestion_report__hero p {
  margin: 10px 0 0;
  color: var(--text_secondary);
}

.suggestion_report__risk_badge {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.suggestion_report__risk_badge--high {
  background: rgba(210, 123, 120, 0.18);
  color: #ffd2d1;
}

.suggestion_report__risk_badge--medium {
  background: rgba(210, 166, 85, 0.18);
  color: #ffe2ae;
}

.suggestion_report__risk_badge--low,
.suggestion_report__risk_badge--neutral {
  background: rgba(95, 208, 165, 0.16);
  color: #d9fff0;
}

.suggestion_report__overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
}

.suggestion_report__overview_item {
  min-width: 0;
  padding: 14px 12px;
  background: rgba(9, 17, 27, 0.72);
}

.suggestion_report__overview_item span {
  display: block;
  color: var(--text_muted);
  font-size: 11px;
}

.suggestion_report__overview_item strong {
  display: block;
  margin-top: 8px;
  color: var(--text_primary);
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
}

.suggestion_report__section_header {
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.suggestion_report__section_header h4 {
  margin: 0;
  color: var(--text_primary);
  font-size: 14px;
  font-weight: 500;
}

.suggestion_report__list {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.suggestion_report__list li {
  position: relative;
  padding-left: 14px;
  line-height: 1.75;
}

.suggestion_report__list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 10px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(79, 194, 220, 0.82);
}

.suggestion_report__empty {
  color: var(--text_secondary);
}

.warning_modal__panel {
  width: min(620px, calc(100vw - 48px));
  max-height: calc(100vh - 72px);
  padding: 18px;
  overflow: hidden;
}

.warning_modal__header {
  align-items: flex-start;
}

.warning_modal__subtitle {
  margin: 4px 0 0;
  color: var(--text_muted);
  font-size: 11px;
  letter-spacing: 0.08em;
}

.warning_modal__content {
  margin-top: 16px;
  display: grid;
  gap: 14px;
  max-height: calc(100vh - 190px);
  overflow-y: auto;
  padding-right: 6px;
}

.warning_modal__hero,
.warning_modal__section {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
  padding: 16px;
}

.warning_modal__hero_header,
.warning_modal__section_header,
.warning_modal__item_header,
.warning_modal__item_footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.warning_modal__badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 124, 124, 0.16);
  color: #ffd7d7;
  font-size: 12px;
  font-weight: 600;
}

.warning_modal__hero h3,
.warning_modal__section h4 {
  margin: 0;
}

.warning_modal__hero h3 {
  margin-top: 12px;
  font-size: 20px;
  line-height: 1.4;
}

.warning_modal__hero p,
.warning_modal__item p {
  margin: 10px 0 0;
  color: var(--text_secondary);
  line-height: 1.7;
  font-size: 12px;
}

.warning_modal__link,
.warning_modal__item_link {
  color: var(--accent_cyan);
  text-decoration: none;
  font-size: 12px;
}

.warning_modal__link {
  display: inline-block;
  margin-top: 12px;
}

.warning_modal__section_header span,
.warning_modal__item_header span,
.warning_modal__item_footer span {
  color: var(--text_muted);
  font-size: 11px;
}

.warning_modal__list {
  margin-top: 12px;
  display: grid;
  gap: 10px;
}

.warning_modal__item {
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  padding: 12px;
}

@media (max-width: 1200px) {
  .screen_shell {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }

  .screen_body {
    grid-template-columns: 1fr;
  }

  .screen_column--left,
  .screen_column--right,
  .screen_footer {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  .screen_column--left,
  .screen_column--right {
    display: grid;
  }

  .screen_column--center {
    min-height: 520px;
  }
}

@media (max-width: 920px) {
  .screen_header {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }

  .screen_header__time_panel {
    align-items: center;
  }

  .screen_footer {
    grid-template-columns: 1fr;
  }

  .map_panel__legend {
    flex-direction: column;
    align-items: stretch;
  }

  .warning_modal__hero_header,
  .warning_modal__section_header,
  .warning_modal__item_header,
  .warning_modal__item_footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .suggestion_report__hero {
    flex-direction: column;
  }

  .suggestion_report__overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
